import * as XLSX from "xlsx";
import type { AnalyticsDashboardData } from "./types";

/**
 * Export analytics dashboard data to a multi-sheet Excel file.
 * Pure function (no React) — follows the Shopify Attribution Dashboard export pattern.
 */
export function exportAnalyticsToExcel(
  data: AnalyticsDashboardData,
  agentName: string,
  periodLabel: string,
): void {
  const workbook = XLSX.utils.book_new();

  // Sheet 1: Overview
  appendOverviewSheet(workbook, data);

  // Sheet 2: Activity Timeline
  appendActivityTimelineSheet(workbook, data);

  // Sheet 3: Categories
  appendCategoriesSheet(workbook, data);

  // Sheet 4: Resolution Quality
  appendResolutionQualitySheet(workbook, data);

  // Sheet 5: Tool Usage
  appendToolUsageSheet(workbook, data);

  // Sheet 6: Pipeline & Outcomes
  appendPipelineOutcomesSheet(workbook, data);

  // Sheet 7: Response Time
  appendResponseTimeSheet(workbook, data);

  const safeName = agentName.replace(/[^a-zA-Z0-9_-]/g, "_").substring(0, 40);
  const filename = `email-analytics-${safeName}-${periodLabel}.xlsx`;
  XLSX.writeFile(workbook, filename);
}

// ---------------------------------------------------------------------------
// Sheet builders
// ---------------------------------------------------------------------------

function appendOverviewSheet(
  wb: XLSX.WorkBook,
  data: AnalyticsDashboardData,
): void {
  const kpiRows = data.kpiCards.map((c) => ({
    Metric: c.label,
    Value: c.value,
    "% of Total": `${c.pct.toFixed(1)}%`,
    "Trend vs Prev Period": `${c.trend >= 0 ? "+" : ""}${c.trend.toFixed(1)}%`,
  }));

  const effRow = {
    Metric: "Automation Rate",
    Value: `${(data.efficiency.automationRate * 100).toFixed(1)}%`,
    "% of Total": "",
    "Trend vs Prev Period": "",
  };

  const fteRow = {
    Metric: "Equivalent FTE",
    Value: data.efficiency.equivalentFte.toFixed(2),
    "% of Total": "",
    "Trend vs Prev Period": "",
  };

  const timeSavedRow = {
    Metric: "Time Saved (hours)",
    Value: (data.efficiency.timeSavedMin / 60).toFixed(1),
    "% of Total": "",
    "Trend vs Prev Period": "",
  };

  const breakdownRows = [
    {
      Metric: "  Auto-Sent savings",
      Value: `${data.timeSavedBreakdown.autoSent.count} emails`,
      "% of Total": `${(data.timeSavedBreakdown.autoSent.minutesSaved / 60).toFixed(1)} hrs`,
      "Trend vs Prev Period": "",
    },
    {
      Metric: "  Draft savings",
      Value: `${data.timeSavedBreakdown.drafts.count} emails`,
      "% of Total": `${(data.timeSavedBreakdown.drafts.minutesSaved / 60).toFixed(1)} hrs`,
      "Trend vs Prev Period": "",
    },
    {
      Metric: "  Skipped savings",
      Value: `${data.timeSavedBreakdown.skipped.count} emails`,
      "% of Total": `${(data.timeSavedBreakdown.skipped.minutesSaved / 60).toFixed(1)} hrs`,
      "Trend vs Prev Period": "",
    },
    {
      Metric: "  Escalation savings",
      Value: `${data.timeSavedBreakdown.handovers.count} emails`,
      "% of Total": `${(data.timeSavedBreakdown.handovers.minutesSaved / 60).toFixed(1)} hrs`,
      "Trend vs Prev Period": "",
    },
    {
      Metric: "  Error overhead",
      Value: `${data.timeSavedBreakdown.errors.count} emails`,
      "% of Total": `-${(data.timeSavedBreakdown.errors.minutesLost / 60).toFixed(1)} hrs`,
      "Trend vs Prev Period": "",
    },
  ];

  const sheet = XLSX.utils.json_to_sheet([
    ...kpiRows,
    effRow,
    fteRow,
    timeSavedRow,
    ...breakdownRows,
  ]);
  XLSX.utils.book_append_sheet(wb, sheet, "Overview");
}

function appendActivityTimelineSheet(
  wb: XLSX.WorkBook,
  data: AnalyticsDashboardData,
): void {
  const rows = data.activityTimeline.map((d) => ({
    Date: d.date,
    Inbound: d.inbound,
    "Auto-Sent": d.sent,
    Drafts: d.drafts,
    Escalations: d.handovers,
    Errors: d.errors,
  }));
  const sheet = XLSX.utils.json_to_sheet(rows);
  XLSX.utils.book_append_sheet(wb, sheet, "Activity Timeline");
}

function appendCategoriesSheet(
  wb: XLSX.WorkBook,
  data: AnalyticsDashboardData,
): void {
  const rows = data.categoryPerformance.map((c) => ({
    Category: c.category,
    Total: c.total,
    "Auto-Sent": c.autoSent,
    Draft: c.draft,
    Escalation: c.handover,
    Skipped: c.skipped,
    "Avg Confidence": c.avgConfidence.toFixed(2),
  }));
  const sheet = XLSX.utils.json_to_sheet(rows);
  XLSX.utils.book_append_sheet(wb, sheet, "Categories");
}

function appendResolutionQualitySheet(
  wb: XLSX.WorkBook,
  data: AnalyticsDashboardData,
): void {
  const rows = data.resolutionQuality.map((r) => ({
    Category: r.label,
    Count: r.count,
    Percentage: `${r.pct.toFixed(1)}%`,
  }));
  const sheet = XLSX.utils.json_to_sheet(rows);
  XLSX.utils.book_append_sheet(wb, sheet, "Resolution Quality");
}

function appendToolUsageSheet(
  wb: XLSX.WorkBook,
  data: AnalyticsDashboardData,
): void {
  const toolRows = data.toolUsage.tools.map((t) => ({
    Tool: t.label,
    Calls: t.calls,
    "Success Rate": `${(t.successRate * 100).toFixed(1)}%`,
    "Avg Duration (ms)": t.avgDurationMs,
  }));

  const kbRow = {
    Tool: "Knowledge Base",
    Calls: data.toolUsage.kbHits,
    "Success Rate":
      data.toolUsage.kbTotal > 0
        ? `${((data.toolUsage.kbHits / data.toolUsage.kbTotal) * 100).toFixed(1)}% hit rate`
        : "N/A",
    "Avg Duration (ms)": "",
  };

  const sheet = XLSX.utils.json_to_sheet([...toolRows, kbRow]);
  XLSX.utils.book_append_sheet(wb, sheet, "Tool Usage");
}

function appendPipelineOutcomesSheet(
  wb: XLSX.WorkBook,
  data: AnalyticsDashboardData,
): void {
  const pipelineRows = data.pipelineStages.map((s) => ({
    Section: "Pipeline Stage",
    Name: s.label,
    Count: s.count,
    Percentage: "",
  }));

  const outcomeRows = data.replyOutcomes.map((o) => ({
    Section: "Reply Outcome",
    Name: o.label,
    Count: o.count,
    Percentage: `${o.pct.toFixed(1)}%`,
  }));

  const sheet = XLSX.utils.json_to_sheet([...pipelineRows, ...outcomeRows]);
  XLSX.utils.book_append_sheet(wb, sheet, "Pipeline & Outcomes");
}

function appendResponseTimeSheet(
  wb: XLSX.WorkBook,
  data: AnalyticsDashboardData,
): void {
  const timings = data.responseTimings ?? [];
  if (timings.length === 0) {
    const sheet = XLSX.utils.json_to_sheet([{ Note: "No response time data available" }]);
    XLSX.utils.book_append_sheet(wb, sheet, "Response Time");
    return;
  }

  const respondedTimes = timings
    .filter((r) => r.responseTimeMs != null)
    .map((r) => r.responseTimeMs!);

  const avg = respondedTimes.length > 0
    ? respondedTimes.reduce((s, v) => s + v, 0) / respondedTimes.length
    : 0;

  const sorted = [...respondedTimes].sort((a, b) => a - b);
  const mid = Math.floor(sorted.length / 2);
  const med = sorted.length === 0 ? 0
    : sorted.length % 2 !== 0 ? sorted[mid]
    : (sorted[mid - 1] + sorted[mid]) / 2;

  const p95Idx = sorted.length > 0 ? Math.max(0, Math.ceil(0.95 * sorted.length) - 1) : 0;
  const p95 = sorted.length > 0 ? sorted[p95Idx] : 0;

  const slaMs = (data.responseTimeConfig?.sla_threshold_hours ?? 24) * 3_600_000;
  const overSla = timings.filter((r) => r.responseTimeMs != null && r.responseTimeMs > slaMs).length;
  const pending = timings.filter((r) => r.responseType === "pending").length;

  const fmtHrs = (ms: number) => `${(ms / 3_600_000).toFixed(2)} hrs`;

  const summaryRows = [
    { Metric: "Average Response Time", Value: fmtHrs(avg) },
    { Metric: "Median Response Time", Value: fmtHrs(med) },
    { Metric: "P95 Response Time", Value: fmtHrs(p95) },
    { Metric: `Over SLA (${data.responseTimeConfig?.sla_threshold_hours ?? 24}h)`, Value: String(overSla) },
    { Metric: "Pending (no response)", Value: String(pending) },
    { Metric: "Total Emails", Value: String(timings.length) },
  ];

  const detailRows = timings.map((r) => ({
    From: r.fromEmail ?? "",
    Subject: r.subject ?? "",
    Type: r.responseType,
    "Response Time (hrs)": r.responseTimeMs != null ? (r.responseTimeMs / 3_600_000).toFixed(2) : "Pending",
    "Received At": r.receivedAt ?? "",
  }));

  const sheet = XLSX.utils.json_to_sheet(summaryRows);
  XLSX.utils.sheet_add_json(sheet, detailRows, { origin: `A${summaryRows.length + 3}` });
  XLSX.utils.book_append_sheet(wb, sheet, "Response Time");
}
